class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

const HEADER_MENU_HIDDEN_FRAME = { opacity: 0, transform: 'translateY(-1.5rem)' };
const HEADER_MENU_VISIBLE_FRAME = { opacity: 1, transform: 'translateY(0)' };
// fill: 'both' keeps the submenu at the keyframe end states outside the
// animation window; the CSS baseline leaves it hidden even while [open], so
// nothing paints before the animation takes over (prevents an open flash).
const HEADER_MENU_OPEN_TIMING = { duration: 200, easing: 'cubic-bezier(0.33, 1, 0.68, 1)', fill: 'both' };
const HEADER_MENU_CLOSE_TIMING = { duration: 150, easing: 'cubic-bezier(0.32, 0, 0.67, 0)', fill: 'both' };

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.isClosing = false;
    this.mainDetailsToggle.querySelector('summary').addEventListener('click', this.onSummaryClick.bind(this));
  }

  onSummaryClick(event) {
    if (!this.mainDetailsToggle.open) return;
    event.preventDefault();

    if (this.isClosing) {
      // Reopen mid-close: detach onfinish so the pending close never lands,
      // then animate from the current state back to fully visible (a plain
      // reverse() would only return to where the close started, which may
      // itself be a partially-faded state).
      this.currentAnimation.onfinish = null;
      this.isClosing = false;
      this.playAnimation([this.currentFrame(), HEADER_MENU_VISIBLE_FRAME], HEADER_MENU_OPEN_TIMING);
      // global.js's summary click handler toggles aria-expanded to false on
      // this same click; the menu is staying open, so correct it after the
      // dispatch completes regardless of listener order.
      queueMicrotask(() => this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', true));
    } else {
      this.close();
    }
  }

  onToggle() {
    if (this.mainDetailsToggle.open) {
      this.closeOtherMenus();
      this.playAnimation([HEADER_MENU_HIDDEN_FRAME, HEADER_MENU_VISIBLE_FRAME], HEADER_MENU_OPEN_TIMING);
    }

    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }

  currentFrame() {
    const computed = getComputedStyle(this.content);
    return {
      opacity: computed.opacity,
      transform: computed.transform === 'none' ? 'translateY(0)' : computed.transform,
    };
  }

  playAnimation(keyframes, timing) {
    if (this.currentAnimation) this.currentAnimation.cancel();
    if (this.reducedMotion.matches) return null;
    this.currentAnimation = this.content.animate(keyframes, timing);
    return this.currentAnimation;
  }

  close() {
    if (!this.mainDetailsToggle.open || this.isClosing) return;

    const finish = () => {
      this.isClosing = false;
      super.close();
    };

    // Start from the current animated state so closing mid-open doesn't jump
    // to fully visible first.
    const animation = this.playAnimation([this.currentFrame(), HEADER_MENU_HIDDEN_FRAME], HEADER_MENU_CLOSE_TIMING);
    if (!animation) {
      finish();
      return;
    }

    this.isClosing = true;
    animation.onfinish = finish;
    animation.oncancel = () => (this.isClosing = false);
  }

  closeOtherMenus() {
    document.querySelectorAll('header-menu').forEach((menu) => {
      if (menu !== this) menu.close();
    });
  }
}

customElements.define('header-menu', HeaderMenu);

document.addEventListener('click', (event) => {
  document.querySelectorAll('header-menu details[open]').forEach((details) => {
    const menu = details.closest('header-menu');
    if (!menu.contains(event.target)) menu.close();
  });
});